import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, ChevronDown, Database, FolderOpen, Eye, EyeOff, Puzzle, Link2 } from 'lucide-react';
import { Modal } from '../components/ui/Modal';
import { useExtensionStore, getBridge } from './store';
import { isBundledExtension } from './bundledIds';
import { DbBrandIcon } from './dbBrandIcons';
import ExtensionModalTextBlock, { TEXT_BLOCK_TYPES } from './ExtensionModalTextBlock';

// Icons an `iconSelect` option may reference by name — official brand marks
// where we have them, tinted with the app accent color. Extensions send
// names, never components — same host-owns-the-pixels rule as the sidebar
// tree.
function OptionIcon({ name, size = 28 }) {
  const brand = <DbBrandIcon name={name} size={size} />;
  if (brand && ['mysql', 'postgres', 'sqlite'].includes(name)) return brand;
  if (name === 'folder') return <FolderOpen size={size} style={{ color: 'var(--accent)' }} aria-hidden />;
  if (name === 'link') return <Link2 size={size} style={{ color: 'var(--accent)' }} aria-hidden />;
  return <Database size={size} style={{ color: 'var(--accent)' }} aria-hidden />;
}

const DEFAULT_COLUMN_TYPES = [
  'INT',
  'BIGINT',
  'VARCHAR(255)',
  'TEXT',
  'DATETIME',
  'DATE',
  'BOOLEAN',
  'DECIMAL(10,2)',
  'FLOAT',
  'JSON',
];

function emptyColumn(typeOptions) {
  return {
    name: '',
    type: typeOptions?.[0] || 'VARCHAR(255)',
    nullable: true,
    primaryKey: false,
    unique: false,
    autoIncrement: false,
    default: '',
  };
}

function defaultValueFor(field) {
  if (field.type === 'columns') {
    return field.defaultValue || [emptyColumn(field.typeOptions)];
  }
  if (field.type === 'checkbox') return !!field.defaultValue;
  if (field.type === 'iconSelect') return field.defaultValue ?? null;
  return field.defaultValue ?? '';
}

// Per-column feature toggles a `columns` field may enable via its `features`
// array (defaults keep the original two for back-compat with older bundles).
const DEFAULT_COLUMN_FEATURES = ['nullable', 'primaryKey'];
const COLUMN_FLAGS = [
  { key: 'nullable', label: 'Nullable' },
  { key: 'primaryKey', label: 'Primary key' },
  { key: 'unique', label: 'Unique' },
  { key: 'autoIncrement', label: 'Auto increment' },
];

/**
 * Password field with a themed reveal toggle. Reuses the app-wide
 * `pw-input-wrap` / `pw-eye-btn` pattern (settings modal) so the eye matches
 * the rest of the app instead of WebView2's native dark `::-ms-reveal`
 * control, which the accompanying CSS suppresses. Keeps the `modal-input`
 * class so it lines up visually with the sibling text fields.
 */
function PasswordField({ value, placeholder, onChange, autoFocus }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="pw-input-wrap">
      <input
        type={reveal ? 'text' : 'password'}
        className="modal-input"
        style={{ width: '100%' }}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
      />
      <button
        type="button"
        className="pw-eye-btn"
        tabIndex={-1}
        onClick={() => setReveal((r) => !r)}
        aria-label="Toggle password visibility"
      >
        {reveal ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

/**
 * Longest guest-supplied title/subtitle the host will render. Without a cap an
 * extension could push the attribution strip off the top of a scrolled modal
 * with a wall of text, which is the same attack as not showing it at all.
 */
const MAX_SPEC_TEXT = 140;

/** Guest-supplied display text: single line, length-capped, never markup. */
function safeSpecText(value) {
  if (typeof value !== 'string') return '';
  const oneLine = value.replace(/[\r\n\t]+/g, ' ').trim();
  return oneLine.length > MAX_SPEC_TEXT ? `${oneLine.slice(0, MAX_SPEC_TEXT - 1)}…` : oneLine;
}

/**
 * Mandatory, host-owned provenance for a modal an extension asked for.
 *
 * The whole risk of `ui.openModal` is that it renders app-native: real Modal
 * chrome, real styling, real position, and — before this — nothing anywhere
 * saying whose dialog it was. A "Your session expired, re-enter your vault
 * passphrase" form was indistinguishable from a first-party one.
 *
 * So: this strip is rendered by the host, always, outside the `spec`-driven
 * region, from the REGISTERED manifest (looked up by the host-injected
 * extensionId — the guest cannot rename itself), in styling the guest has no
 * way to influence. An extension can lie in its own title; it cannot remove,
 * restyle, cover or reword this.
 *
 * EXCEPT for first-party bundled extensions. Their dialogs ARE Apiboo asking
 * — the DB Manager's connection form is a first-party surface, and stamping
 * "Apiboo will never ask for your passwords here" under a first-party
 * password field was self-contradictory noise. The exemption keys on
 * isBundledExtension — the compiled-in id set — which a third-party
 * extension cannot join: it is decided by what shipped in the binary, never
 * by anything the guest sends.
 */
function ExtensionAttribution({ extensionId }) {
  const manifest = useExtensionStore((s) => s.extensions[extensionId]);
  const name = manifest?.name || extensionId;
  const publisher = manifest?.publisher;
  if (isBundledExtension(extensionId)) return null;
  return (
    <div className="ext-modal-attribution" role="note">
      <Puzzle size={14} className="ext-modal-attribution-icon" aria-hidden />
      <span className="ext-modal-attribution-text">
        This dialog was requested by the extension{' '}
        <span className="ext-modal-attribution-name">{name}</span>
        {publisher ? ` (${publisher})` : ''} — {extensionId}. Apiboo will never ask for your
        passwords here.
      </span>
    </div>
  );
}

function FlagCheckbox({ label, checked, onChange }) {
  return (
    <label className="ext-flag-label">
      <input type="checkbox" className="checkbox-brand" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

/**
 * Editable type combo, same interaction as RequestBuilder's HttpMethodPicker:
 * free-text input (so `VARCHAR(255)` can be edited to `VARCHAR(100)`) plus a
 * chevron opening a portaled preset list. Reuses the method-picker-* dropdown
 * styling; its z-index (10030+) portals above the modal overlay (z-50).
 */
function TypeCombo({ value, options, onChange, style }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 160 });
  const wrapRef = useRef(null);

  const measure = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 160) });
  }, []);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => measure();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Don't let the modal's own Escape handler close the whole form while
        // the dropdown is what the user means to dismiss.
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, measure]);

  return (
    <>
      <div ref={wrapRef} className="ext-type-combo" style={style}>
        <input
          type="text"
          className="modal-input"
          style={{ paddingRight: 28 }}
          value={value ?? ''}
          spellCheck={false}
          autoComplete="off"
          placeholder="Type"
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onBlur={(e) => {
            if (!e.target.value.trim()) onChange(options?.[0] || '');
          }}
        />
        <button
          type="button"
          className="ext-type-combo-chevron"
          tabIndex={-1}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Choose preset type"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronDown size={12} strokeWidth={2.5} aria-hidden />
        </button>
      </div>
      {open &&
        createPortal(
          <>
            <div
              className="method-picker-backdrop"
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                setOpen(false);
              }}
            />
            <div
              className="method-picker-dropdown"
              role="listbox"
              style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.width, zIndex: 10040 }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="method-picker-list">
                {(options || []).map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="option"
                    aria-selected={value === t}
                    className={`method-picker-item ${value === t ? 'method-picker-item--active' : ''}`}
                    onClick={() => {
                      onChange(t);
                      setOpen(false);
                    }}
                  >
                    <span className="method-picker-item-label">{t}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

/**
 * Generic host-rendered modal for `sdk.ui.openModal(spec)` — extensions send
 * a declarative field spec (never markup), the host renders a real app-styled
 * Modal, mirroring how the sidebar tree contract keeps pixels host-owned.
 * Mounted once globally in App.js alongside <ExtensionHostLayer/>.
 */
export default function ExtensionModalHost() {
  const pendingModal = useExtensionStore((s) => s.pendingModal);
  const [values, setValues] = useState({});
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!pendingModal) return;
    const initial = {};
    for (const field of pendingModal.spec?.fields || []) {
      initial[field.id] = defaultValueFor(field);
    }
    setValues(initial);
  }, [pendingModal]);

  if (!pendingModal) return null;

  const { extensionId, requestId, spec } = pendingModal;
  const fields = spec?.fields || [];

  function resolve(result) {
    getBridge(extensionId)?.resolveModalRequest(requestId, result);
  }

  function updateField(id, value) {
    setValues((prev) => ({ ...prev, [id]: value }));
  }

  function updateColumn(id, idx, patch) {
    setValues((prev) => {
      const cols = [...(prev[id] || [])];
      cols[idx] = { ...cols[idx], ...patch };
      return { ...prev, [id]: cols };
    });
  }

  function addColumn(id, typeOptions) {
    setValues((prev) => {
      const cols = [...(prev[id] || [])];
      cols.push(emptyColumn(typeOptions));
      return { ...prev, [id]: cols };
    });
    // The new card renders at the bottom of the capped scroll body — bring it
    // into view, or repeated adds happen invisibly below the fold. rAF runs
    // after React commits the new card, so scrollHeight already includes it.
    requestAnimationFrame(() => {
      bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
    });
  }

  function removeColumn(id, idx) {
    setValues((prev) => {
      const cols = [...(prev[id] || [])];
      cols.splice(idx, 1);
      return { ...prev, [id]: cols };
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (submitDisabled) return; // Enter in a field must not skip the gate
    resolve(values);
  }

  function handleClose() {
    resolve(null);
  }

  const submitDisabled = fields.some(
    (f) =>
      (f.required && f.type !== 'checkbox' && !String(values[f.id] ?? '').trim()) ||
      // Type-to-confirm (e.g. DROP TABLE): exact match, checked live — the
      // guest never sees a submit it has to reject afterwards.
      (typeof f.mustEqual === 'string' && String(values[f.id] ?? '') !== f.mustEqual)
  );
  // A picker-only modal answers on click — a Save button would just be a
  // second, initially-disabled way to do the same thing.
  const hideSubmit = fields.length > 0 && fields.every((f) => f.type === 'iconSelect' && f.submitOnSelect);
  // Modals dismissible via X/Escape may opt out of the redundant Cancel button.
  const hideCancel = !!spec?.hideCancel;

  // Extensions may ask for a wider modal (e.g. the column editor) — value is
  // whitelisted against the sizes Modal actually supports.
  const modalSize = ['md', 'lg', 'xl'].includes(spec?.size) ? spec.size : 'md';

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title={safeSpecText(spec?.title)}
      subtitle={safeSpecText(spec?.subtitle)}
      size={modalSize}
      preventBackdropClose={!!spec?.preventBackdropClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Host-owned, unconditional, and OUTSIDE the scrolling field body so
            it cannot be scrolled out of view by a long spec. */}
        <ExtensionAttribution extensionId={extensionId} />
        {/* Fields scroll inside a capped body (long column lists in New
            table); the Cancel/Create footer stays pinned below. Scrollbar
            styling is ext-modal-body's own — panel-scroll's light
            scrollbar-color reads as a default scrollbar on dark modals in
            newer WebKit, which honors the standard props over the
            ::-webkit-scrollbar pseudos. */}
        <div ref={bodyRef} className="ext-modal-body space-y-4">
        {fields.map((field) => (
          <div key={field.id}>
            {field.type !== 'checkbox' && !(TEXT_BLOCK_TYPES.has(field.type) && !field.label) && (
              <label
                className="modal-logo-label"
                // Icon cards want more air under the label than plain inputs.
                style={{ display: 'block', marginBottom: field.type === 'iconSelect' ? '0.875rem' : '0.375rem' }}
              >
                {field.label}
                {field.required ? ' *' : ''}
              </label>
            )}
            {TEXT_BLOCK_TYPES.has(field.type) ? (
              <ExtensionModalTextBlock field={field} />
            ) : field.type === 'iconSelect' ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.min((field.options || []).length, 3)}, 1fr)`,
                  gap: 10,
                }}
              >
                {(field.options || []).map((opt) => {
                  const selected = values[field.id] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        updateField(field.id, opt.value);
                        // submitOnSelect: the pick itself answers the modal
                        // (e.g. "which database engine?" before a second modal
                        // asks for that engine's connection fields).
                        if (field.submitOnSelect) resolve({ ...values, [field.id]: opt.value });
                      }}
                      className={`ext-icon-option${selected ? ' ext-icon-option--selected' : ''}`}
                    >
                      <OptionIcon name={opt.icon} size={30} />
                      <span className="text-xs font-medium text-gray-800 dark:text-zinc-200">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : field.type === 'file' ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={values[field.id] ?? ''}
                  onChange={(e) => updateField(field.id, e.target.value)}
                  placeholder={field.placeholder || ''}
                  className="modal-input"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button
                  type="button"
                  className="modern-button"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid var(--line-strong, rgba(128,128,128,0.25))',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 12.5,
                  }}
                  onClick={async () => {
                    try {
                      const { open } = await import('@tauri-apps/plugin-dialog');
                      // `directory: true` on the field spec turns this into a
                      // folder picker (e.g. choosing where to create a new
                      // SQLite database file).
                      const picked = await open({
                        multiple: false,
                        directory: !!field.directory,
                        filters: field.directory ? undefined : field.fileFilters || undefined,
                      });
                      if (typeof picked === 'string' && picked) updateField(field.id, picked);
                    } catch (err) {
                      console.warn('[extensions] file picker failed:', err);
                    }
                  }}
                >
                  <FolderOpen size={14} /> Browse
                </button>
              </div>
            ) : field.type === 'columns' ? (
              (() => {
                const features = field.features || DEFAULT_COLUMN_FEATURES;
                const flags = COLUMN_FLAGS.filter((f) => features.includes(f.key));
                const hasDefault = features.includes('default');
                const cols = values[field.id] || [];
                return (
                  <div>
                    <div className="space-y-2">
                      {cols.map((col, idx) => (
                        <div key={idx} className="ext-col-card">
                          <div className="ext-field-row">
                            <input
                              type="text"
                              placeholder="Column name"
                              className="modal-input"
                              style={{ flex: 2, minWidth: 0 }}
                              value={col.name}
                              onChange={(e) => updateColumn(field.id, idx, { name: e.target.value })}
                            />
                            <TypeCombo
                              style={{ flex: 1.4, minWidth: 0 }}
                              value={col.type}
                              options={field.typeOptions || DEFAULT_COLUMN_TYPES}
                              onChange={(t) => updateColumn(field.id, idx, { type: t })}
                            />
                            {hasDefault && (
                              <input
                                type="text"
                                placeholder="Default (optional)"
                                className="modal-input"
                                style={{ flex: 1.4, minWidth: 0 }}
                                value={col.default ?? ''}
                                onChange={(e) => updateColumn(field.id, idx, { default: e.target.value })}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => removeColumn(field.id, idx)}
                              className="ext-remove-col-btn"
                              title="Remove column"
                              disabled={cols.length <= 1}
                            >
                              <X size={14} />
                            </button>
                          </div>
                          {flags.length > 0 && (
                            <div className="ext-col-flags">
                              {flags.map((flag) => (
                                <FlagCheckbox
                                  key={flag.key}
                                  label={flag.label}
                                  checked={!!col[flag.key]}
                                  onChange={(e) => updateColumn(field.id, idx, { [flag.key]: e.target.checked })}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => addColumn(field.id, field.typeOptions)}
                      className="ext-add-col-btn"
                    >
                      <Plus size={12} /> Add column
                    </button>
                  </div>
                );
              })()
            ) : field.type === 'select' ? (
              <select
                className="modal-input"
                style={{ width: '100%' }}
                value={values[field.id] ?? ''}
                onChange={(e) => updateField(field.id, e.target.value)}
              >
                {(field.options || []).map((opt) => (
                  <option key={opt.value ?? opt} value={opt.value ?? opt}>{opt.label ?? opt}</option>
                ))}
              </select>
            ) : field.type === 'checkbox' ? (
              <label className="ext-checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-brand"
                  checked={!!values[field.id]}
                  onChange={(e) => updateField(field.id, e.target.checked)}
                />
                <span className="ext-checkbox-text">
                  <span>{field.label}</span>
                  {/* What the switch does, in a sentence — text only. */}
                  {typeof field.hint === 'string' && field.hint && <span className="ext-checkbox-hint">{field.hint}</span>}
                </span>
              </label>
            ) : field.type === 'password' ? (
              <PasswordField
                value={values[field.id]}
                placeholder={field.placeholder || ''}
                onChange={(v) => updateField(field.id, v)}
                autoFocus={field === fields[0]}
              />
            ) : (
              <input
                type={field.type === 'number' ? 'number' : 'text'}
                className="modal-input"
                style={{ width: '100%' }}
                placeholder={field.placeholder || ''}
                value={values[field.id] ?? ''}
                onChange={(e) => updateField(field.id, e.target.value)}
                autoFocus={field === fields[0]}
              />
            )}
          </div>
        ))}
        </div>

        {!(hideCancel && hideSubmit) && (
          <div className="modal-footer-row">
            {!hideCancel && (
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
              >
                Cancel
              </button>
            )}
            {!hideSubmit && (
              <button
                type="submit"
                disabled={submitDisabled}
                // `.modern-button` paints the accent background over any bg-*
                // class, so the danger variant carries its own class instead.
                className={`px-3 py-1.5 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm ${
                  spec?.submitTone === 'danger' ? 'ext-modal-danger-btn' : 'bg-blue-600 hover:bg-blue-700 modern-button'
                }`}
              >
                {spec?.submitLabel || 'Save'}
              </button>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}
